// DepthFindingCard — Gate G3.7 Phase 6 (plan §8.2).
//
// Renders one SRD Depth Probe finding as a four-option card:
//
//   * (A) Approve  — apply the probe's proposed EARS clause verbatim.
//   * (B) Edit     — supply a custom EARS clause (in-line editor).
//   * (C) Defer    — record an out-of-scope reason; no SRD edit.
//   * (D) Reject   — false-positive feedback for rubric tuning.
//
// Layout follows the binding mockup in plan §8.2:
//
//   ┌──────────────────────────────────────────────────────────────┐
//   │ F-001 · 🔴 CRITICAL · iot/watchdog          [Pass 2: ✓ closed] │
//   │ ────────────────────────────────────────────────────────────  │
//   │ ❗ No watchdog timeout SLA                                     │
//   │                                                                │
//   │ EVIDENCE                                                       │
//   │ "SR-014 says 'shall recover from faults' without a deadline."  │
//   │                                                                │
//   │ SUGGESTED RESOLUTION  (kind: edit_sr — Sonnet proposed; …)    │
//   │ Replace SR-014 body with:                                      │
//   │   The system shall reset within 250 ms when the watchdog       │
//   │   timer is not kicked for more than 100 ms.                    │
//   │ Linked SRs: SR-014                                             │
//   │                                                                │
//   │ [✓ Approve]  [✎ Edit]  [↩ Defer]  [✗ Reject]                  │
//   └──────────────────────────────────────────────────────────────┘
//
// When ``resolution_kind === 'architect_only'``, the suggested-
// resolution block becomes a free-text editor with the probe's
// question as a label. The architect's reply lands as the new SR
// body via the Edit option.
//
// The card emits its decision through ``onAnswer({ optionId, text })``
// — the parent maps that to the wire format the depth_resolver
// expects (``status`` + ``answer`` colon-separated form). See
// ContentAwareG37Gate for the parent wiring.

import { useEffect, useState } from 'react'
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  Edit3,
  HelpCircle,
  RotateCcw,
  ShieldAlert,
  ShieldX,
  X,
  XCircle,
} from 'lucide-react'

import type { GateDecisionRecord } from '@/types'
import {
  type ClosureStatus,
  G37_OPTION_APPROVE,
  G37_OPTION_DEFER,
  G37_OPTION_EDIT,
  G37_OPTION_PROMOTE,
  G37_OPTION_REJECT,
  type G37DecisionPayload,
  type G37Option,
} from '@/types/depth'

// --------------------------------------------------------------------------- //
// Severity styling
// --------------------------------------------------------------------------- //
const SEVERITY_STYLE: Record<
  string,
  { chipClass: string; icon: typeof AlertTriangle; emoji: string }
> = {
  critical: {
    chipClass:
      'bg-red-950/40 border-red-700/50 text-red-200',
    icon: AlertTriangle,
    emoji: '🔴',
  },
  high: {
    chipClass:
      'bg-amber-950/40 border-amber-700/50 text-amber-200',
    icon: AlertTriangle,
    emoji: '🟠',
  },
  medium: {
    chipClass:
      'bg-yellow-950/40 border-yellow-700/50 text-yellow-200',
    icon: AlertTriangle,
    emoji: '🟡',
  },
  low: {
    chipClass: 'bg-slate-800/60 border-slate-700/40 text-slate-300',
    icon: HelpCircle,
    emoji: '⚪',
  },
}

const CLOSURE_STYLE: Record<
  ClosureStatus,
  { label: string; chipClass: string; icon: typeof CheckCircle2 }
> = {
  closed: {
    label: 'Pass 2: ✓ closed',
    chipClass: 'border-emerald-700/50 bg-emerald-950/40 text-emerald-300',
    icon: CheckCircle2,
  },
  unchanged: {
    label: 'Pass 2: ⚠ unchanged',
    chipClass: 'border-amber-700/50 bg-amber-950/40 text-amber-200',
    icon: AlertTriangle,
  },
  regressed: {
    label: 'Pass 2: ✗ regressed',
    chipClass: 'border-red-700/50 bg-red-950/40 text-red-200',
    icon: XCircle,
  },
  not_evaluated: {
    label: 'Pass 2: — not evaluated',
    chipClass: 'border-slate-700/50 bg-slate-900/40 text-slate-400',
    icon: HelpCircle,
  },
  deferred: {
    label: 'Pass 2: deferred',
    chipClass: 'border-slate-700/50 bg-slate-900/40 text-slate-400',
    icon: HelpCircle,
  },
  rejected_by_architect: {
    label: 'Pass 2: rejected',
    chipClass: 'border-slate-700/50 bg-slate-900/40 text-slate-500',
    icon: ShieldX,
  },
}

// --------------------------------------------------------------------------- //
// Public props
// --------------------------------------------------------------------------- //
export interface DepthFindingCardProps {
  /** The wrapping gate decision record (carries id + status + answer). */
  decision: GateDecisionRecord
  /**
   * Inline finding payload — pulled from ``decision.payload``. We
   * pass it explicitly so the parent can validate the cargo shape
   * once, instead of every card narrowing the same union.
   */
  payload: G37DecisionPayload
  /**
   * Pass-2 closure status for this finding. Optional — only present
   * once pass-2 has run. Drives the closure badge in the header.
   */
  pass2Status?: ClosureStatus | null
  /**
   * Architect's local manual-edit / defer-reason buffer. Lifted to
   * the parent so the text survives a card unmount when the panel
   * filters or paginates.
   */
  manualText: string
  setManualText: (value: string) => void
  /**
   * Resolver diagnostic for this decision from the last failed
   * submit (``null`` when nothing failed or this row was valid).
   * When present, the card renders an inline error banner so the
   * architect doesn't have to hunt across 26 rows to find the
   * blocking row.
   */
  submissionError?: string | null
  /**
   * Apply an option to the decision. The parent translates this
   * into a ``GateDecisionRecord`` patch (``status`` + ``answer``
   * colon-separated wire form for the depth_resolver).
   */
  onAnswer: (args: { option: G37Option; text: string | null }) => void
}

// --------------------------------------------------------------------------- //
// Component
// --------------------------------------------------------------------------- //
export function DepthFindingCard({
  decision,
  payload,
  pass2Status,
  manualText,
  setManualText,
  submissionError,
  onAnswer,
}: DepthFindingCardProps) {
  const severity = (payload.severity || 'medium').toLowerCase()
  const sevStyle = SEVERITY_STYLE[severity] ?? SEVERITY_STYLE.medium
  const SeverityIcon = sevStyle.icon

  // Decode the architect's current pick from ``decision.answer``.
  // The wire format is either ``<option_id>`` or ``<option_id>:<text>``.
  // We carry the raw answer through the parent so we don't lose
  // partial state while the architect is still typing.
  const selectedOptionId = decodeOptionId(decision)
  const selectedOption =
    payload.options.find((o) => o.id === selectedOptionId) ?? null
  const needsText = Boolean(selectedOption?.needs_text)

  // Auto-expand the editor when the architect picks a needs_text
  // option but hasn't typed anything yet.
  const [editorOpen, setEditorOpen] = useState(needsText)
  useEffect(() => {
    if (needsText) setEditorOpen(true)
  }, [needsText])

  const closure = pass2Status ? CLOSURE_STYLE[pass2Status] : null
  const isArchitectOnly = payload.resolution_kind === 'architect_only'

  return (
    // Plan W4.2 — testid skeleton for the G3.7 per-finding row.
    // ``gate-37-row`` lets a Playwright spec target the card without
    // scraping copy. The 4 option buttons + the inline editor carry
    // their own testids (added below) so the spec can drive every
    // choice in the four-option panel.
    <div
      className={`alm-card alm-fadein ${
        submissionError
          ? 'ring-1 ring-rose-500/60 border-rose-500/40'
          : ''
      }`}
      data-testid="gate-37-row"
      data-submission-error={submissionError ? 'true' : undefined}
    >
      {submissionError && (
        <div
          className="mb-2 flex items-start gap-2 rounded-md border border-rose-700/40 bg-rose-950/30 px-2.5 py-2 text-[11.5px] text-rose-200"
          data-testid="gate-37-row-error"
          role="alert"
        >
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="font-semibold mb-0.5">
              This row blocked the last submit
            </div>
            <div className="text-rose-200/90 leading-snug whitespace-pre-line">
              {submissionError}
            </div>
          </div>
        </div>
      )}
      {/* ---- Header row: id · severity · category · pass-2 chip ---- */}
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1.5">
            <span className="font-mono text-[10.5px] text-indigo-400">
              {payload.finding_id}
            </span>
            <span
              className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${sevStyle.chipClass}`}
              title={`Severity: ${severity}`}
            >
              <SeverityIcon className="h-3 w-3" />
              {severity}
            </span>
            {payload.category && (
              <span className="alm-chip alm-chip-info text-[10px]">
                {payload.category}
              </span>
            )}
            {payload.resolution_kind === 'architect_only' && (
              <span
                className="alm-chip alm-chip-warn text-[10px]"
                title="The probe couldn't propose an EARS clause that passed validation. You'll need to write one by hand."
              >
                architect_only
              </span>
            )}
            {payload.resolution_kind === 'defer_ok' && (
              <span
                className="alm-chip text-[10px]"
                title="The probe explicitly judged this gap acceptable to defer."
              >
                defer_ok
              </span>
            )}
          </div>
          <h3 className="text-sm font-semibold text-slate-100 leading-snug">
            {sevStyle.emoji} {payload.title}
          </h3>
        </div>
        {closure && (
          <span
            className={`inline-flex items-center gap-1 shrink-0 rounded-md border px-2 py-1 text-[10px] font-semibold ${closure.chipClass}`}
          >
            <closure.icon className="h-3 w-3" />
            {closure.label}
          </span>
        )}
      </div>

      {/* ---- Evidence block ---- */}
      {payload.evidence && (
        <div className="mb-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
            Evidence
          </div>
          <blockquote className="border-l-2 border-slate-700 pl-2.5 text-[12.5px] text-slate-300 italic leading-relaxed">
            {payload.evidence}
          </blockquote>
        </div>
      )}

      {/* ---- Expected (when present) ---- */}
      {payload.expected && (
        <div className="mb-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
            Expected
          </div>
          <p className="text-[12.5px] text-slate-300 leading-relaxed">
            {payload.expected}
          </p>
        </div>
      )}

      {/* ---- Suggested resolution / probe question ---- */}
      {isArchitectOnly && payload.probe_question && (
        <div className="mb-3 rounded-md border border-amber-700/40 bg-amber-950/15 p-2.5">
          <div className="text-[10px] uppercase tracking-wider text-amber-300/80 mb-1">
            Probe question (architect-only)
          </div>
          <p className="text-[12.5px] text-amber-100 leading-relaxed">
            {payload.probe_question}
          </p>
        </div>
      )}
      {payload.suggested_patch && (
        <div className="mb-3 rounded-md border border-slate-700/40 bg-slate-950/40 p-2.5">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-[10px] uppercase tracking-wider text-slate-500">
              {isArchitectOnly ? 'Suggested resolution (architect must verify)' : 'Suggested resolution'}
            </span>
            <span
              className="alm-chip text-[9.5px]"
              title="Resolution kind from the probe envelope"
            >
              kind: {payload.resolution_kind}
            </span>
            {!isArchitectOnly && (
              <span
                className="alm-chip text-[9.5px]"
                title="The probe's clause already passed the EARS-grammar guard."
              >
                ✓ EARS-validated
              </span>
            )}
          </div>
          <pre className="whitespace-pre-wrap text-[12.5px] text-slate-100 font-sans leading-relaxed border-l-2 border-indigo-500/50 pl-2.5">
            {payload.suggested_patch}
          </pre>
          {payload.linked_srs.length > 0 && (
            <div className="text-[10.5px] text-slate-500 mt-1.5">
              <span className="uppercase tracking-wide text-slate-600 mr-1">
                Linked SRs:
              </span>
              <span className="font-mono text-slate-300">
                {payload.linked_srs.join(', ')}
              </span>
            </div>
          )}
        </div>
      )}

      {/* ---- Action row ---- */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5 mb-2">
        {payload.options.map((option) => {
          const selected = option.id === selectedOptionId
          return (
            <OptionButton
              key={option.id}
              option={option}
              selected={selected}
              onClick={() => {
                if (option.needs_text) {
                  setEditorOpen(true)
                  // Persist the option id so submitters know which
                  // path the architect chose, even if the text is
                  // still empty. The validate pass on submit will
                  // reject empty-text needs_text picks.
                  onAnswer({ option, text: manualText.trim() || null })
                } else {
                  onAnswer({ option, text: null })
                }
              }}
            />
          )
        })}
      </div>

      {/* ---- Inline editor (Edit / Defer) ---- */}
      {(needsText || (selectedOption?.needs_text && editorOpen)) && (
        <InlineEditor
          option={selectedOption!}
          value={manualText}
          onChange={(value) => {
            setManualText(value)
            // Mirror the typed text into the decision answer on
            // every keystroke so the panel's "all decided" check
            // stays in sync without a save action.
            onAnswer({ option: selectedOption!, text: value.trim() || null })
          }}
        />
      )}
    </div>
  )
}

// --------------------------------------------------------------------------- //
// Sub-components
// --------------------------------------------------------------------------- //
function OptionButton({
  option,
  selected,
  onClick,
}: {
  option: G37Option
  selected: boolean
  onClick: () => void
}) {
  // Pick an icon + tone from the option id rather than the label so
  // we don't have to parse the human-readable copy.
  const Icon = iconForOption(option.id)
  const tone = toneForOption(option.id)
  const selectedClass = selected
    ? tone === 'destructive'
      ? 'border-red-500/70 bg-red-950/30 ring-1 ring-red-500/40 text-red-100'
      : tone === 'success'
      ? 'border-emerald-500/70 bg-emerald-950/30 ring-1 ring-emerald-500/40 text-emerald-100'
      : tone === 'amber'
      ? 'border-amber-500/70 bg-amber-950/30 ring-1 ring-amber-500/40 text-amber-100'
      : 'border-indigo-500/70 bg-indigo-950/30 ring-1 ring-indigo-500/40 text-indigo-100'
    : 'border-slate-700/60 bg-slate-900/40 hover:border-slate-600 hover:bg-slate-900/70 text-slate-200'

  return (
    <button
      type="button"
      data-testid={`gate-37-option-${option.id}`}
      onClick={onClick}
      className={`group flex flex-col items-start gap-1 rounded-md border px-2.5 py-2 text-left transition ${selectedClass}`}
      title={option.summary}
    >
      <div className="flex items-center gap-1.5 text-[11.5px] font-semibold">
        <Icon className="h-3 w-3" />
        <span>{option.label}</span>
      </div>
      <p className="text-[10.5px] leading-tight opacity-80 line-clamp-2">
        {option.summary}
      </p>
    </button>
  )
}

function InlineEditor({
  option,
  value,
  onChange,
}: {
  option: G37Option
  value: string
  onChange: (next: string) => void
}) {
  const isEdit = option.id === G37_OPTION_EDIT
  const isReject = option.id === G37_OPTION_REJECT
  const isPromote = option.id === G37_OPTION_PROMOTE
  const label = isEdit
    ? 'Your replacement EARS clause (required)'
    : isReject
      ? 'Your rejection rationale (required for Critical/High)'
      : isPromote
        ? "Stakeholder (optional) · leave blank if you don't know yet — the DU is still tracked for follow-up · use a `|` to add an optional note"
        : 'Your defer reason (required)'
  const placeholder = isEdit
    ? 'e.g. The system shall reset within 250 ms when the watchdog timer is not kicked for more than 100 ms.'
    : isReject
      ? 'e.g. False positive — the rubric flagged tenancy isolation but SR-031 already covers it via RLS.'
      : isPromote
        ? 'Leave blank if unknown — or e.g. Platform Lead | Decision pending Q3 strategy review.'
        : 'e.g. Out of v1 scope, tracked in JIRA-1234.'
  const testId = isEdit
    ? 'gate-37-edit-text'
    : isReject
      ? 'gate-37-reject-text'
      : isPromote
        ? 'gate-37-promote-text'
        : 'gate-37-defer-text'
  return (
    <div className="rounded-md border border-amber-700/40 bg-amber-950/10 p-2.5 mt-2">
      <div className="text-[10px] uppercase tracking-wider text-amber-300/70 mb-1.5">
        {label}
      </div>
      <textarea
        data-testid={testId}
        className="w-full min-h-[72px] rounded-md border border-amber-700/40 bg-slate-950/60 px-3 py-2 text-[12.5px] text-slate-100 font-mono focus:outline-none focus:ring-1 focus:ring-amber-500/50"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {isEdit && (
        <p className="text-[10.5px] text-amber-300/70 mt-1 leading-snug">
          Must follow EARS form (Ubiquitous, Event-Driven, State-Driven,
          Optional, or Unwanted-Behavior). The deterministic EARS-grammar
          guard validates your clause on submit; invalid clauses fail
          the gate with a 422.
        </p>
      )}
      {isReject && (
        <p className="text-[10.5px] text-amber-300/70 mt-1 leading-snug">
          Critical and High severity rejects require ≥ 20 characters of
          rationale (W3.1 hardening). The text feeds back into the rubric
          tuning ledger so the next probe run can drop the bad item.
        </p>
      )}
      {isPromote && (
        <p className="text-[10.5px] text-amber-300/70 mt-1 leading-snug">
          Option (E) is the system's <strong>"I don't know yet"</strong>{' '}
          escape — submit succeeds even if the stakeholder field is
          empty. The finding lands as a Deferred Unknown
          (DU-G37-{`<finding>`}) in §3a of the exported requirements
          pack and is surfaced as an Open item to downstream agents
          on subsequent runs until resolved. Leaving the stakeholder
          blank records it as <code>(unknown)</code> — the next run's
          punch-list of items needing owner assignment.
        </p>
      )}
    </div>
  )
}

// --------------------------------------------------------------------------- //
// Helpers
// --------------------------------------------------------------------------- //
function decodeOptionId(decision: GateDecisionRecord): string | null {
  const answer = (decision.answer ?? '').trim()
  if (!answer) {
    // Map status onto the option id so a freshly-set status (without
    // an explicit answer write) still highlights the right pill.
    switch (decision.status) {
      case 'approved':
        return G37_OPTION_APPROVE
      case 'edited':
        return G37_OPTION_EDIT
      case 'skipped':
        return G37_OPTION_DEFER
      case 'rejected':
        return G37_OPTION_REJECT
      default:
        return null
    }
  }
  if (answer.startsWith('{')) {
    try {
      const obj = JSON.parse(answer)
      if (obj && typeof obj.option_id === 'string') return obj.option_id
    } catch {
      /* fall through */
    }
  }
  if (answer.includes(':')) {
    return answer.slice(0, answer.indexOf(':')).trim()
  }
  return answer
}

function iconForOption(optionId: string): typeof Check {
  switch (optionId) {
    case G37_OPTION_APPROVE:
      return Check
    case G37_OPTION_EDIT:
      return Edit3
    case G37_OPTION_DEFER:
      return RotateCcw
    case G37_OPTION_REJECT:
      return X
    case G37_OPTION_PROMOTE:
      return HelpCircle
    default:
      return ShieldAlert
  }
}

function toneForOption(
  optionId: string,
): 'success' | 'destructive' | 'amber' | 'default' {
  switch (optionId) {
    case G37_OPTION_APPROVE:
      return 'success'
    case G37_OPTION_REJECT:
      return 'destructive'
    case G37_OPTION_EDIT:
    case G37_OPTION_DEFER:
    case G37_OPTION_PROMOTE:
      return 'amber'
    default:
      return 'default'
  }
}
